import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, MapPin, Plus, Trash } from "@phosphor-icons/react";
import { EmptyState } from "@/components/Shared";
import { fmtMoney } from "@/lib/apiClient";

/**
 * GoodsReceiptLines — extracted from GoodsReceipt.jsx (iter48 refactor).
 * Editable line-items table with per-row destination routing (warehouse vs site).
 *
 * Props:
 *   • lines[]     — line items (each with _key, description, quantity, unit_price, unit, hsn, destination, warehouse_id, project_id)
 *   • updateLine(idx, patch) — apply partial update to a line
 *   • addLine() / removeLine(idx) — mutate line list
 *   • warehouses[] / projects[] — dropdown options
 *   • defaultWarehouse — used when line destination flips to warehouse
 */
export default function GoodsReceiptLines({ lines, updateLine, addLine, removeLine, warehouses, projects, defaultWarehouse }) {
  return (
    <section className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold bg-slate-800 text-white px-1.5 py-0.5 rounded-sm">Step 3</span>
          <span className="text-[15px] font-semibold font-display">Line Items</span>
          <span className="text-[11px] text-slate-500">· {lines.length} rows</span>
        </div>
        <Button onClick={addLine} variant="outline" size="sm" data-testid="add-line" className="rounded-sm border-slate-300">
          <Plus size={13} className="mr-1" /> Add row
        </Button>
      </div>
      {lines.length === 0 ? (
        <EmptyState title="No lines yet" description="Upload a vendor bill above to auto-fill, or click Add row to enter manually." />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="label-caps w-8">#</TableHead>
                <TableHead className="label-caps min-w-[220px]">Description</TableHead>
                <TableHead className="label-caps">Qty</TableHead>
                <TableHead className="label-caps">Unit</TableHead>
                <TableHead className="label-caps">Rate</TableHead>
                <TableHead className="label-caps">HSN</TableHead>
                <TableHead className="label-caps">Destination</TableHead>
                <TableHead className="label-caps min-w-[180px]">Target</TableHead>
                <TableHead className="label-caps text-right">Total</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l, i) => {
                const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                return (
                  <TableRow key={l._key} data-testid={`grn-line-${i}`}>
                    <TableCell className="text-slate-400 text-[11px]">{i + 1}</TableCell>
                    <TableCell><Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="h-8 text-xs" data-testid={`line-desc-${i}`} /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="h-8 w-20 text-xs font-mono-num" data-testid={`line-qty-${i}`} /></TableCell>
                    <TableCell><Input value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} className="h-8 w-16 text-xs" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} className="h-8 w-24 text-xs font-mono-num" data-testid={`line-rate-${i}`} /></TableCell>
                    <TableCell><Input value={l.hsn} onChange={(e) => updateLine(i, { hsn: e.target.value })} className="h-8 w-16 text-xs" placeholder="—" /></TableCell>
                    <TableCell>
                      <Select
                        value={l.destination}
                        onValueChange={(v) => updateLine(i, { destination: v, warehouse_id: v === "warehouse" ? (l.warehouse_id || defaultWarehouse) : "", project_id: v === "site" ? l.project_id : "" })}
                      >
                        <SelectTrigger className="h-8 text-xs w-32" data-testid={`line-destination-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warehouse"><Buildings size={12} className="inline mr-1" /> Warehouse</SelectItem>
                          <SelectItem value="site"><MapPin size={12} className="inline mr-1" /> Site</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {l.destination === "warehouse" ? (
                        <Select value={l.warehouse_id || "__none__"} onValueChange={(v) => updateLine(i, { warehouse_id: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`line-warehouse-${i}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select value={l.project_id || "__none__"} onValueChange={(v) => updateLine(i, { project_id: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`line-project-${i}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(lineTotal)}</TableCell>
                    <TableCell>
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`line-remove-${i}`}>
                        <Trash size={13} />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
